import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from './Login';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── CONSTANTES ───────────────────────────────────────────────────────────────
const statusConfig = {
  agendado:      { label: 'Agendado',       color: '#b45309', bg: '#fef3c7', dot: '#f59e0b', border: '#f59e0b', next: 'confirmado',     nextLabel: '✅ Confirmar' },
  confirmado:    { label: 'Confirmado',     color: '#1d4ed8', bg: '#dbeafe', dot: '#3b82f6', border: '#3b82f6', next: 'em_atendimento', nextLabel: '💇 Iniciar' },
  em_atendimento:{ label: 'Em atendimento', color: '#7c3aed', bg: '#ede9fe', dot: '#8b5cf6', border: '#8b5cf6', next: 'concluido',      nextLabel: '🎉 Finalizar' },
  concluido:     { label: 'Concluído',      color: '#047857', bg: '#d1fae5', dot: '#10b981', border: '#10b981', next: null,             nextLabel: null },
  cancelado:     { label: 'Cancelado',      color: '#b91c1c', bg: '#fee2e2', dot: '#ef4444', border: '#ef4444', next: null,             nextLabel: null },
  bloqueio:      { label: 'Bloqueio',       color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af', border: '#9ca3af', next: null,             nextLabel: null },
};

const MESES     = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const MESES_ABR = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const DIAS_EXT  = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
const DIAS_ABR  = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const DIAS_MIN  = ['D','S','T','Q','Q','S','S'];

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const fmtHour = (date) =>
  new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const fmtDate = (date) => {
  const d = new Date(date);
  return `${DIAS_ABR[d.getDay()]}, ${d.getDate()} ${MESES_ABR[d.getMonth()]}`;
};

const buildCalendar = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  const cells = [];
  for (let i = 0; i < firstDay.getDay(); i++)
    cells.push({ date: new Date(year, month, -firstDay.getDay() + i + 1), currentMonth: false });
  for (let d = 1; d <= lastDay.getDate(); d++)
    cells.push({ date: new Date(year, month, d), currentMonth: true });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ date: new Date(year, month + 1, d), currentMonth: false });
  return cells;
};

const getWeekDays = (ref) => {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    return dd;
  });
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 48px;
  padding-bottom: 6rem;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 48px;
  z-index: 20;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #f3f4f6;
`;
const Tab = styled.button`
  flex: 1;
  padding: 0.7rem 0;
  border: none;
  background: none;
  font-size: 0.82rem;
  font-weight: ${p => p.active ? '700' : '500'};
  color: ${p => p.active ? '#334155' : '#9ca3af'};
  border-bottom: 2px solid ${p => p.active ? '#334155' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s;
`;

// ── Nav bar (dia / semana / mês) ──────────────────────────────────────────────
const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.25rem;
`;
const NavTitle = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
`;
const NavBtn = styled.button`
  width: 32px; height: 32px;
  border-radius: 50%; border: none;
  background: #f3f4f6; color: #374151;
  font-size: 1rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:active { background: #e5e7eb; }
`;
const TodayBtn = styled.button`
  font-size: 0.72rem; font-weight: 600;
  color: #334155; background: #f1f5f9;
  border: none; border-radius: 20px;
  padding: 0.25rem 0.7rem; cursor: pointer;
`;
const AddBtn = styled.button`
  background: #334155; color: white;
  border: none; border-radius: 50%;
  width: 30px; height: 30px; font-size: 1.1rem;
  cursor: pointer; display: flex;
  align-items: center; justify-content: center;
`;
const AddMenu = styled.div`
  position: absolute; top: 38px; right: 0; z-index: 50;
  background: white; border-radius: 0.6rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  min-width: 180px; overflow: hidden;
`;
const AddMenuItem = styled.button`
  display: block; width: 100%; text-align: left;
  padding: 0.7rem 1rem; border: none; background: none;
  font-size: 0.85rem; color: #1f2937; cursor: pointer;
  &:hover, &:active { background: #f3f4f6; }
  & + & { border-top: 1px solid #f3f4f6; }
`;

// ── Semana (mini strip) ───────────────────────────────────────────────────────
const WeekStripContainer = styled.div`
  display: flex;
  padding: 0 1.25rem 0.5rem;
`;
const WeekStripGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;
const WeekCell = styled.div`
  display: flex; flex-direction: column;
  align-items: center; gap: 3px; cursor: pointer;
`;
const WeekLabel = styled.span`
  font-size: 0.6rem; font-weight: 600;
  color: ${p => p.sun ? '#ef4444' : '#9ca3af'};
  text-transform: uppercase;
`;
const WeekNum = styled.div`
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8rem;
  font-weight: ${p => p.isToday || p.sel ? '700' : '400'};
  background: ${p => p.sel ? '#334155' : p.isToday ? '#e2e8f0' : 'transparent'};
  color: ${p => p.sel ? 'white' : p.isToday ? '#334155' : p.sun ? '#ef4444' : '#374151'};
`;
const WeekDot = styled.div`
  width: 4px; height: 4px; border-radius: 50%;
  background: ${p => p.color || '#e5e7eb'};
`;

const WeekGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-image: linear-gradient(#d1d5db 1px, transparent 1px);
  background-size: 100% 60px;
`;

const WeekCol = styled.div`
  position: relative;
  border-right: 1px solid #d1d5db;
  min-height: ${24 * 60}px;
`;

// ── Mês (calendário) ──────────────────────────────────────────────────────────
const DaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0 0.5rem 0.25rem;
`;
const DayLabel = styled.div`
  text-align: center;
  font-size: 0.6rem; font-weight: 600;
  color: ${p => p.sun ? '#ef4444' : '#9ca3af'};
  padding: 0.2rem 0;
`;
const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  padding: 0 0.5rem 0.5rem;
`;
const DayCell = styled.div`
  aspect-ratio: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: flex-start;
  padding-top: 4px;
  border-radius: 10px;
  cursor: pointer;
  background: ${p => p.sel ? '#334155' : p.isToday ? '#e2e8f0' : 'transparent'};
  transition: background 0.15s;
`;
const DayNum = styled.span`
  font-size: 0.78rem;
  font-weight: ${p => p.isToday || p.sel ? '700' : '400'};
  color: ${p => p.sel ? 'white' : p.isToday ? '#334155' : p.other ? '#d1d5db' : p.sun ? '#ef4444' : '#374151'};
  margin-bottom: 2px;
`;
const DotsRow = styled.div`
  display: flex; gap: 2px; justify-content: center; flex-wrap: wrap; max-width: 26px;
`;
const Dot = styled.span`
  width: 4px; height: 4px; border-radius: 50%;
  background: ${p => p.sel ? 'rgba(255,255,255,0.8)' : p.color};
`;

// ── Layout ────────────────────────────────────────────────────────────────────
const Content = styled.div`
  padding: 0.75rem 1.25rem;
`;

const TimeTag = styled.div`
  font-size: 0.7rem; font-weight: 700;
  color: ${p => statusConfig[p.status]?.color};
  background: ${p => statusConfig[p.status]?.bg};
  padding: 0.2rem 0.45rem; border-radius: 6px;
  white-space: nowrap; flex-shrink: 0; text-align: center; line-height: 1.4;
`;
const DetailRow = styled.div`
  display: flex; gap: 0.5rem; align-items: flex-start;
  margin-bottom: 0.35rem; font-size: 0.78rem; color: #374151;
  span.label { color: #9ca3af; min-width: 65px; }
`;
const ActionBar = styled.div`
  display: flex; gap: 0.4rem; margin-top: 0.65rem;
`;
const ActionBtn = styled.button`
  flex: 1; padding: 0.55rem; border-radius: 8px; border: none;
  cursor: pointer; font-size: 0.78rem; font-weight: 600;
  &.primary { background: #334155; color: white; }
  &.danger  { background: #fee2e2; color: #dc2626; }
`;



// ── Overlay / Sheet ───────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 300;
  display: flex; align-items: flex-end; justify-content: center;
`;
const Sheet = styled(motion.div)`
  background: #fff; border-radius: 1.2rem 1.2rem 0 0;
  padding: 1.5rem; width: 100%; max-width: 480px;
  max-height: 90vh; overflow-y: auto;
  h3 { margin: 0 0 1rem; font-size: 1rem; color: #1f1f1f; }
  label { font-size: 0.78rem; color: #555; display: block; margin-bottom: 0.2rem; }
  input, textarea, select {
    width: 100%; padding: 0.6rem 0.8rem;
    border: 1.5px solid #e5e7eb; border-radius: 0.5rem;
    font-size: 0.9rem; margin-bottom: 0.8rem; box-sizing: border-box;
    &:focus { border-color: #334155; outline: none; }
  }
  textarea { resize: vertical; min-height: 55px; }
`;
const SheetRow = styled.div`display: flex; gap: 0.6rem;`;
const SheetSave = styled.button`
  background: #334155; color: white; border: none; border-radius: 0.6rem;
  padding: 0.75rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; flex: 2;
  &:disabled { opacity: 0.55; }
`;
const SheetCancel = styled.button`
  background: #f3f4f6; color: #555; border: none; border-radius: 0.6rem;
  padding: 0.75rem; font-size: 0.88rem; cursor: pointer; flex: 1;
`;

// ─── TIMELINE (VIEW DIA) ──────────────────────────────────────────────────────
const TimelineScroll = styled.div`
  height: calc(100vh - 270px);
  overflow-y: auto;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-bottom: 1rem;
  background: white;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
`;
const TimelineWrapper = styled.div`
  display: flex;
  position: relative;
  min-height: ${24 * 60}px;
`;
const TimeCol = styled.div`
  width: 50px;
  background: #f8f9fa;
  border-right: 1px solid #d1d5db;
  display: flex;
  flex-direction: column;
`;
const HourSlot = styled.div`
  height: 60px;
  display: flex; justify-content: center; padding-top: 0.3rem;
  font-size: 0.65rem; color: #6b7280; font-weight: 600; box-sizing: border-box;
`;
const EventsCol = styled.div`
  flex: 1;
  position: relative;
  background-image: linear-gradient(#d1d5db 1px, transparent 1px);
  background-size: 100% 60px;
`;
const EventBlock = styled(motion.div)`
  position: absolute;
  border-radius: 6px;
  padding: 0.35rem 0.5rem;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid ${p => p.borderColor || p.dotColor};
  border-left: 3px solid ${p => p.dotColor};
  background: ${p => p.bg};
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  display: flex; flex-direction: column;

  .title { font-size: 0.75rem; font-weight: 700; color: ${p => p.color}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .time { font-size: 0.65rem; color: ${p => p.color}; opacity: 0.85; margin-top: 0.1rem; }
`;
const CurrentTimeLine = styled.div`
  position: absolute; left: 0; right: 0; height: 2px; background: #ef4444; z-index: 10; pointer-events: none;
  &::before { content: ''; position: absolute; left: -4px; top: -4px; width: 10px; height: 10px; border-radius: 50%; background: #ef4444; }
`;
const GhostBlock = styled.div`
  position: absolute; left: 6px; right: 6px; z-index: 50;
  background: rgba(51, 65, 85, 0.12);
  border: 2px dashed #334155;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  pointer-events: none;
  span { font-size: 0.7rem; font-weight: 600; color: #334155; }
`;
const DragMenuPopup = styled.div`
  position: absolute; left: 6px; right: 6px; z-index: 60;
  display: flex; gap: 6px; padding: 6px;
  background: white; border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.18);
`;
const DragMenuBtn = styled.button`
  flex: ${p => p.cancel ? '0 0 36px' : '1'};
  padding: 0.55rem 0; border: none; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  background: ${p => p.primary ? '#334155' : p.cancel ? '#f3f4f6' : '#e0e7ff'};
  color: ${p => p.primary ? 'white' : p.cancel ? '#9ca3af' : '#334155'};
`;


// ─── SHEET DETALHES DO AGENDAMENTO (dia / semana) ─────────────────────────────
function ApptDetailsSheet({ appt, onClose, onAdvance, onCancel }) {
  const isBlock = appt.isBlock;
  const cfg = isBlock ? statusConfig['bloqueio'] : (statusConfig[appt.status] || statusConfig['agendado']);
  return (
    <Overlay onClick={onClose}>
      <Sheet initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.2rem', fontSize: '1.1rem' }}>{isBlock ? 'Horario bloqueado' : appt.clientName}</h3>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{isBlock ? (appt.notes || 'Sem motivo') : appt.service}</div>
          </div>
          <TimeTag status={isBlock ? 'cancelado' : appt.status}>{cfg.label}</TimeTag>
        </div>

        <DetailRow><span className="label">Data</span><span>{fmtDate(appt.datetime)} as {fmtHour(appt.datetime)}</span></DetailRow>
        <DetailRow><span className="label">Duracao</span><span>{appt.duration} min</span></DetailRow>
        {!isBlock && <DetailRow><span className="label">Valor</span><span style={{ fontWeight: 700, color: '#1f2937' }}>R$ {(appt.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></DetailRow>}
        {!isBlock && appt.clientPhone && <DetailRow><span className="label">Contato</span><span>{appt.clientPhone}</span></DetailRow>}
        {!isBlock && appt.notes && <DetailRow><span className="label">Obs.</span><span>{appt.notes}</span></DetailRow>}

        {isBlock ? (
          <ActionBar style={{ marginTop: '1.5rem' }}>
            <ActionBtn className="danger" onClick={async (e) => {
              const removed = await onCancel(e, appt._id);
              if (removed) onClose();
            }}>
              Remover bloqueio
            </ActionBtn>
          </ActionBar>
        ) : cfg.next && (
          <ActionBar style={{ marginTop: '1.5rem' }}>
            <ActionBtn className="primary" onClick={async (e) => {
              await onAdvance(e, appt._id);
              onClose();
            }}>
              {cfg.nextLabel}
            </ActionBtn>
            <ActionBtn className="danger" onClick={async (e) => {
              const removed = await onCancel(e, appt._id);
              if (removed) onClose();
            }}>
              Cancelar agendamento
            </ActionBtn>
          </ActionBar>
        )}
      </Sheet>
    </Overlay>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const Pedidos = () => {
  const now = new Date();
  const { token } = useAuth();
  const [view, setView]           = useState('dia');   // 'dia' | 'semana' | 'mes'
  const [selected, setSelected]   = useState(now);
  const [viewYear, setViewYear]   = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [expanded, setExpanded]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showAddMenu, setShowAddMenu]     = useState(false);
  const timelineRef = useRef(null);
  const eventsColRef = useRef(null);
  const weekGridRef = useRef(null);
  const weekSwipeRef = useRef(null);

  // Drag-to-create state
  const [dragGhost, setDragGhost] = useState(null);   // { startMin, endMin, dayIndex }
  const [dragMenu, setDragMenu]   = useState(null);    // { startMin, endMin, dayIndex }
  const lastTapRef = useRef(0);
  const dragRef    = useRef(null); // { active, anchorMin, currentMin, dayIndex }

  const authH = { Authorization: `Bearer ${token}` };

  // Busca o mês visível (suficiente para todas as views)
  const fetchMonth = useCallback(async (year, month) => {
    if (!token) return;
    setLoading(true);
    try {
      const m = String(month + 1).padStart(2, '0');
      const r = await fetch(`${API}/appointments?month=${year}-${m}`, { headers: authH });
      if (r.ok) setAppointments(await r.json());
    } catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchMonth(viewYear, viewMonth); }, [viewYear, viewMonth, fetchMonth]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMonth(viewYear, viewMonth);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [viewYear, viewMonth, fetchMonth]);

  /* Dados da agenda por view — manter só este bloco (duplicar causa "Identifier already declared"). */
  const dayAppts = useMemo(
    () =>
      appointments
        .filter(a => isSameDay(new Date(a.datetime), selected) && a.status !== 'cancelado')
        .sort((a, b) => new Date(a.datetime) - new Date(b.datetime)),
    [appointments, selected]
  );

  const weekDays = useMemo(() => getWeekDays(selected), [selected]);

  const weekAppts = useCallback(
    (day) =>
      appointments
        .filter(a => isSameDay(new Date(a.datetime), day) && a.status !== 'cancelado')
        .sort((a, b) => new Date(a.datetime) - new Date(b.datetime)),
    [appointments]
  );

  // Scroll to active time on day/week view (only on view change, not on week swipe)
  const lastScrollViewRef = useRef(null);
  useEffect(() => {
    if ((view === 'dia' || view === 'semana') && timelineRef.current) {
      const viewKey = view === 'dia' ? `dia-${selected.toDateString()}` : `semana`;
      if (lastScrollViewRef.current === viewKey) return;
      lastScrollViewRef.current = viewKey;

      let scrollY = 8 * 60;
      if (view === 'dia' && dayAppts[0]) {
        const d = new Date(dayAppts[0].datetime);
        scrollY = (d.getHours() * 60) + d.getMinutes() - 30;
      } else if (view === 'semana') {
        const allWeekAppts = weekDays.flatMap(d => weekAppts(d));
        if (allWeekAppts.length > 0) {
          const earliest = Math.min(...allWeekAppts.map(a => {
            const d = new Date(a.datetime);
            return d.getHours() * 60 + d.getMinutes();
          }));
          scrollY = earliest - 30;
        }
      }

      if (isSameDay(selected, now) || (view === 'semana' && weekDays.some(d => isSameDay(d, now)))) {
        const todayAppts = view === 'dia' ? dayAppts : weekAppts(now);
        if (todayAppts.length === 0) {
          scrollY = (now.getHours() * 60) + now.getMinutes() - 60;
        }
      }
      timelineRef.current.scrollTop = Math.max(0, scrollY);
    }
  }, [view, selected, dayAppts, weekDays, weekAppts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quando muda de mês (view mês/semana), buscar também mês adjacente se necessário
  const setMonth = (year, month) => {
    setViewYear(year); setViewMonth(month);
  };

  const prevPeriod = () => {
    if (view === 'dia') {
      const d = new Date(selected); d.setDate(d.getDate() - 1); setSelected(d);
      if (d.getMonth() !== viewMonth || d.getFullYear() !== viewYear)
        setMonth(d.getFullYear(), d.getMonth());
    } else if (view === 'semana') {
      const d = new Date(selected); d.setDate(d.getDate() - 7); setSelected(d);
      if (d.getMonth() !== viewMonth || d.getFullYear() !== viewYear)
        setMonth(d.getFullYear(), d.getMonth());
    } else {
      const m = viewMonth === 0 ? 11 : viewMonth - 1;
      const y = viewMonth === 0 ? viewYear - 1 : viewYear;
      setMonth(y, m);
    }
  };

  const nextPeriod = () => {
    if (view === 'dia') {
      const d = new Date(selected); d.setDate(d.getDate() + 1); setSelected(d);
      if (d.getMonth() !== viewMonth || d.getFullYear() !== viewYear)
        setMonth(d.getFullYear(), d.getMonth());
    } else if (view === 'semana') {
      const d = new Date(selected); d.setDate(d.getDate() + 7); setSelected(d);
      if (d.getMonth() !== viewMonth || d.getFullYear() !== viewYear)
        setMonth(d.getFullYear(), d.getMonth());
    } else {
      const m = viewMonth === 11 ? 0 : viewMonth + 1;
      const y = viewMonth === 11 ? viewYear + 1 : viewYear;
      setMonth(y, m);
    }
  };

  const goToday = () => {
    setSelected(now);
    setMonth(now.getFullYear(), now.getMonth());
  };

  const navTitle = () => {
    if (view === 'dia') {
      if (isSameDay(selected, now)) return 'Hoje';
      return `${DIAS_EXT[selected.getDay()]}, ${selected.getDate()} ${MESES_ABR[selected.getMonth()]}`;
    }
    if (view === 'semana') {
      const week = getWeekDays(selected);
      const first = week[0]; const last = week[6];
      if (first.getMonth() === last.getMonth())
        return `${first.getDate()}–${last.getDate()} ${MESES_ABR[first.getMonth()]} ${first.getFullYear()}`;
      return `${first.getDate()} ${MESES_ABR[first.getMonth()]} – ${last.getDate()} ${MESES_ABR[last.getMonth()]}`;
    }
    return `${MESES[viewMonth]} ${viewYear}`;
  };

  const getTimelineEvents = (appts) => {
    let columns = [];
    return appts.map(appt => {
      const d = new Date(appt.datetime);
      const startMins = d.getHours() * 60 + d.getMinutes();
      const endMins = startMins + (appt.duration || 60);

      let colIndex = 0;
      while (columns[colIndex] && columns[colIndex] > startMins) {
        colIndex++;
      }
      columns[colIndex] = endMins;

      return { ...appt, startMins, endMins, colIndex };
    });
  };

  // Ações
  const advanceStatus = async (e, id) => {
    e.stopPropagation();
    const appt = appointments.find(a => a._id === id);
    if (!appt || !statusConfig[appt.status]?.next) return;
    try {
      const r = await fetch(`${API}/appointments/${id}`, {
        method: 'PATCH',
        headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: statusConfig[appt.status].next })
      });
      if (r.ok) {
        const updated = await r.json();
        setAppointments(prev => prev.map(a => a._id === id ? updated : a));
      }
    } catch { /* noop */ }
  };

  /** @returns {Promise<boolean>} true se o usuário confirmou e o cancelamento foi aplicado */
  const cancelAppt = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja cancelar este agendamento? Ele será removido da sua agenda.')) {
      return false;
    }
    try {
      const r = await fetch(`${API}/appointments/${id}`, { method: 'DELETE', headers: authH });
      if (r.ok) {
        setAppointments(prev => prev.map(a => a._id === id ? { ...a, status: 'cancelado' } : a));
        return true;
      }
    } catch {}
    return false;
  };

  // Calendário (view mês)
  const cells = buildCalendar(viewYear, viewMonth);

  const detailAppt = expanded ? appointments.find(a => a._id === expanded) : null;

  // ─── DRAG-TO-CREATE HANDLERS (dia + semana) ────────────────────────────────
  const snap15 = (mins) => Math.round(mins / 15) * 15;

  const getMinsFromEl = useCallback((touchY, el) => {
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return Math.max(0, Math.min(24 * 60 - 1, touchY - rect.top));
  }, []);

  const getDayIndexFromTouch = useCallback((touchX) => {
    const grid = weekGridRef.current;
    if (!grid) return 0;
    const rect = grid.getBoundingClientRect();
    const x = touchX - rect.left;
    const colW = rect.width / 7;
    return Math.max(0, Math.min(6, Math.floor(x / colW)));
  }, []);

  const attachDragListeners = useCallback((el, isDayView) => {
    if (!el) return () => {};

    const onStart = (e) => {
      if (e.target.closest('[data-event-block]')) return;
      const t = Date.now();
      const gap = t - lastTapRef.current;
      lastTapRef.current = t;
      if (gap >= 350) return;

      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const mins = snap15(getMinsFromEl(touch.clientY, el));
      const dayIdx = isDayView ? -1 : getDayIndexFromTouch(touch.clientX);
      dragRef.current = { active: true, anchorMin: mins, currentMin: mins, dayIndex: dayIdx };
      setDragGhost({ startMin: mins, endMin: mins + 30, dayIndex: dayIdx });
      setDragMenu(null);
    };

    const onMove = (e) => {
      if (!dragRef.current?.active) return;
      e.preventDefault();
      const touch = e.touches[0];
      const mins = snap15(getMinsFromEl(touch.clientY, el));
      dragRef.current.currentMin = mins;
      const anchor = dragRef.current.anchorMin;
      const s = Math.min(anchor, mins);
      const en = Math.max(anchor, mins);
      setDragGhost({ startMin: s, endMin: Math.max(en, s + 15), dayIndex: dragRef.current.dayIndex });
    };

    const onEnd = () => {
      if (!dragRef.current?.active) return;
      const { anchorMin, currentMin, dayIndex } = dragRef.current;
      dragRef.current.active = false;
      const s = Math.min(anchorMin, currentMin);
      const en = Math.max(anchorMin, currentMin);
      setDragGhost(null);
      setDragMenu({ startMin: s, endMin: Math.max(en, s + 30), dayIndex });
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [getMinsFromEl, getDayIndexFromTouch]);

  // Attach to day view EventsCol
  useEffect(() => attachDragListeners(eventsColRef.current, true), [view, attachDragListeners]); // eslint-disable-line

  // Attach to week view WeekGrid
  useEffect(() => attachDragListeners(weekGridRef.current, false), [view, attachDragListeners]); // eslint-disable-line

  // ─── WEEK SWIPE (acompanha o dedo com translateX) ─────────────────────────
  const nextPeriodRef = useRef(nextPeriod);
  const prevPeriodRef = useRef(prevPeriod);
  nextPeriodRef.current = nextPeriod;
  prevPeriodRef.current = prevPeriod;

  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeTransition, setSwipeTransition] = useState(false);
  const swipeStateRef = useRef({ startX: 0, startY: 0, swiping: false, decided: false, cooldown: false });

  useEffect(() => {
    const el = weekSwipeRef.current;
    if (!el || view !== 'semana') return;
    const s = swipeStateRef.current;

    const onStart = (e) => {
      if (dragRef.current?.active || s.cooldown) return;
      const t = e.touches[0];
      s.startX = t.clientX;
      s.startY = t.clientY;
      s.swiping = false;
      s.decided = false;
      setSwipeTransition(false);
    };

    const onMove = (e) => {
      if (s.decided && !s.swiping) return;
      if (dragRef.current?.active || s.cooldown) return;
      const t = e.touches[0];
      const dx = t.clientX - s.startX;
      const dy = t.clientY - s.startY;

      if (!s.decided) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        s.decided = true;
        s.swiping = Math.abs(dx) > Math.abs(dy);
        if (!s.swiping) return;
      }
      if (s.swiping && e.cancelable) e.preventDefault();
      if (s.swiping) setSwipeOffset(dx);
    };

    const onEnd = (e) => {
      if (!s.swiping || dragRef.current?.active || s.cooldown) {
        s.swiping = false;
        s.decided = false;
        return;
      }
      const t = e.changedTouches[0];
      const dx = t.clientX - s.startX;
      const threshold = el.clientWidth * 0.2;

      if (Math.abs(dx) > threshold) {
        s.cooldown = true;
        const goNext = dx < 0;
        setSwipeTransition(true);
        setSwipeOffset(goNext ? -el.clientWidth : el.clientWidth);
        setTimeout(() => {
          if (goNext) nextPeriodRef.current(); else prevPeriodRef.current();
          setSwipeOffset(0);
          setSwipeTransition(false);
          s.cooldown = false;
        }, 250);
      } else {
        setSwipeTransition(true);
        setSwipeOffset(0);
        setTimeout(() => setSwipeTransition(false), 250);
      }
      s.swiping = false;
      s.decided = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [view]); // eslint-disable-line

  const handleDragAction = async (action) => {
    if (!dragMenu) return;
    const { startMin, endMin, dayIndex } = dragMenu;
    const duration = endMin - startMin;
    const h = String(Math.floor(startMin / 60)).padStart(2, '0');
    const m = String(startMin % 60).padStart(2, '0');
    const pad = (n) => String(n).padStart(2, '0');
    const targetDate = (dayIndex >= 0) ? weekDays[dayIndex] : selected;
    const dateStr = `${targetDate.getFullYear()}-${pad(targetDate.getMonth() + 1)}-${pad(targetDate.getDate())}T${h}:${m}`;

    setDragMenu(null);

    if (action === 'block') {
      try {
        const r = await fetch(`${API}/appointments/block`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ datetime: new Date(dateStr).toISOString(), duration, notes: '' })
        });
        if (r.ok) {
          const created = await r.json();
          setAppointments(prev => [...prev, created]);
        }
      } catch {}
    } else {
      setShowForm(true);
    }
  };

  return (
    <Page onClick={() => { showAddMenu && setShowAddMenu(false); dragMenu && setDragMenu(null); }}>
      <StickyHeader>
        {/* Tabs */}
        <TabBar>
          <Tab active={view === 'dia'}    onClick={() => { setView('dia'); setDragGhost(null); setDragMenu(null); }}>Dia</Tab>
          <Tab active={view === 'semana'} onClick={() => { setView('semana'); setDragGhost(null); setDragMenu(null); }}>Semana</Tab>
          <Tab active={view === 'mes'}    onClick={() => { setView('mes'); setDragGhost(null); setDragMenu(null); }}>Mês</Tab>
        </TabBar>

        {/* Nav */}
        <NavBar>
          <NavBtn onClick={prevPeriod}>‹</NavBtn>
          <NavTitle>{navTitle()}</NavTitle>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', position: 'relative' }}>
            <TodayBtn onClick={goToday}>Hoje</TodayBtn>
            <AddBtn onClick={() => setShowAddMenu(v => !v)}>+</AddBtn>
            {showAddMenu && (
              <AddMenu>
                <AddMenuItem onClick={() => { setShowAddMenu(false); setShowForm(true); }}>
                  Novo agendamento
                </AddMenuItem>
                <AddMenuItem onClick={() => { setShowAddMenu(false); setShowBlockForm(true); }}>
                  Bloquear horario
                </AddMenuItem>
              </AddMenu>
            )}
          </div>
          <NavBtn onClick={nextPeriod}>›</NavBtn>
        </NavBar>

        {/* Strip de semana (view dia e semana) */}
        {(view === 'dia' || view === 'semana') && (() => {
          const renderStripDays = (days) => (
            <WeekStripGrid>
              {days.map((d, i) => {
                const hasAppt = appointments.some(a => isSameDay(new Date(a.datetime), d) && a.status !== 'cancelado');
                const firstDot = appointments.find(a => isSameDay(new Date(a.datetime), d) && a.status !== 'cancelado');
                const isSel = isSameDay(d, selected);
                const isToday = isSameDay(d, now);
                return (
                  <WeekCell key={i} onClick={() => { setSelected(d); if (view === 'mes' || view === 'semana') setView('dia'); }}>
                    <WeekLabel sun={i === 0}>{DIAS_MIN[i]}</WeekLabel>
                    <WeekNum sel={isSel} isToday={isToday} sun={i === 0}>{d.getDate()}</WeekNum>
                    <WeekDot color={hasAppt ? (statusConfig[firstDot?.status]?.dot || '#334155') : 'transparent'} />
                  </WeekCell>
                );
              })}
            </WeekStripGrid>
          );

          if (view === 'dia') {
            return (
              <WeekStripContainer>
                {renderStripDays(getWeekDays(selected))}
              </WeekStripContainer>
            );
          }

          const prevW = getWeekDays(new Date(selected.getTime() - 7 * 86400000));
          const nextW = getWeekDays(new Date(selected.getTime() + 7 * 86400000));

          return (
            <WeekStripContainer style={{ overflow: 'hidden' }}>
              <div style={{ width: '50px', flexShrink: 0 }} />
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{
                  display: 'flex', width: '300%',
                  transform: `translateX(calc(-33.333% + ${swipeOffset}px))`,
                  transition: swipeTransition ? 'transform 0.25s ease-out' : 'none'
                }}>
                  <div style={{ width: '33.333%', flexShrink: 0 }}>{renderStripDays(prevW)}</div>
                  <div style={{ width: '33.333%', flexShrink: 0 }}>{renderStripDays(getWeekDays(selected))}</div>
                  <div style={{ width: '33.333%', flexShrink: 0 }}>{renderStripDays(nextW)}</div>
                </div>
              </div>
            </WeekStripContainer>
          );
        })()}

        {/* Header do calendário mês */}
        {view === 'mes' && (
          <DaysHeader>
            {DIAS_MIN.map((d, i) => <DayLabel key={i} sun={i === 0}>{d}</DayLabel>)}
          </DaysHeader>
        )}
      </StickyHeader>

      {/* ── VIEW MÊS: calendário ── */}
      {view === 'mes' && (
        <>
          {loading && <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af' }}>Carregando...</p>}
          <CalGrid>
            {cells.map(({ date, currentMonth }, i) => {
              const dayApptsCal = appointments.filter(a =>
                isSameDay(new Date(a.datetime), date) && a.status !== 'cancelado'
              );
              const isSel  = isSameDay(date, selected);
              const isToday = isSameDay(date, now);
              return (
                <DayCell
                  key={i} sel={isSel} isToday={isToday}
                  onClick={() => { setSelected(date); setView('dia'); }}
                >
                  <DayNum sel={isSel} isToday={isToday} other={!currentMonth} sun={i % 7 === 0}>
                    {date.getDate()}
                  </DayNum>
                  <DotsRow>
                    {dayApptsCal.slice(0, 3).map(a => (
                      <Dot key={a._id} color={a.isBlock ? statusConfig['bloqueio'].dot : (statusConfig[a.status]?.dot)} sel={isSel} />
                    ))}
                  </DotsRow>
                </DayCell>
              );
            })}
          </CalGrid>

        </>
      )}

      {/* ── VIEW DIA ── */}
      {view === 'dia' && (
        <Content style={{ padding: '0.75rem 1.25rem 0' }}>
          <TimelineScroll ref={timelineRef}>
            <TimelineWrapper>
              <TimeCol>
                {Array.from({ length: 24 }).map((_, i) => (
                  <HourSlot key={i}>{String(i).padStart(2, '0')}:00</HourSlot>
                ))}
              </TimeCol>
              <EventsCol ref={eventsColRef}>
                {isSameDay(selected, now) && (
                  <CurrentTimeLine style={{ top: now.getHours() * 60 + now.getMinutes() }} />
                )}
                {getTimelineEvents(dayAppts).map(appt => {
                  const isBlock = appt.isBlock;
                  const cfg = isBlock ? statusConfig['bloqueio'] : (statusConfig[appt.status] || statusConfig['agendado']);
                  const height = Math.max(appt.duration || 60, 20);
                  const left = 6 + (appt.colIndex * 15);
                  return (
                    <EventBlock
                      key={appt._id}
                      data-event-block="true"
                      dotColor={cfg.dot} borderColor={isBlock ? '#9ca3af' : cfg.border} bg={isBlock ? '#f3f4f6' : cfg.bg} color={cfg.color}
                      style={{
                        top: appt.startMins, height, left: `${left}px`, right: '6px', zIndex: appt.colIndex + 1,
                        ...(isBlock ? { backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)', borderStyle: 'dashed' } : {})
                      }}
                      onClick={(e) => { e.stopPropagation(); setExpanded(appt._id); }}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    >
                      <div className="title">{isBlock ? 'Bloqueio' : `${appt.clientName} · ${appt.service}`}</div>
                      <div className="time">{fmtHour(appt.datetime)} ({appt.duration} min){isBlock && appt.notes ? ` · ${appt.notes}` : ''}</div>
                    </EventBlock>
                  );
                })}

                {/* Ghost block durante o drag (view dia) */}
                {dragGhost && dragGhost.dayIndex === -1 && (
                  <GhostBlock style={{ top: dragGhost.startMin, height: Math.max(dragGhost.endMin - dragGhost.startMin, 15) }}>
                    <span>{String(Math.floor(dragGhost.startMin / 60)).padStart(2, '0')}:{String(dragGhost.startMin % 60).padStart(2, '0')} — {String(Math.floor(dragGhost.endMin / 60)).padStart(2, '0')}:{String(dragGhost.endMin % 60).padStart(2, '0')}</span>
                  </GhostBlock>
                )}

                {/* Mini-menu ao soltar (view dia) */}
                {dragMenu && dragMenu.dayIndex === -1 && (
                  <DragMenuPopup style={{ top: dragMenu.startMin }}>
                    <DragMenuBtn primary onClick={() => handleDragAction('block')}>Bloquear</DragMenuBtn>
                    <DragMenuBtn onClick={() => handleDragAction('appointment')}>Agendar</DragMenuBtn>
                    <DragMenuBtn cancel onClick={() => setDragMenu(null)}>x</DragMenuBtn>
                  </DragMenuPopup>
                )}
              </EventsCol>
            </TimelineWrapper>
          </TimelineScroll>
        </Content>
      )}

      {/* ── VIEW SEMANA ── */}
      {view === 'semana' && (() => {
        const prevWeek = getWeekDays(new Date(selected.getTime() - 7 * 86400000));
        const nextWeek = getWeekDays(new Date(selected.getTime() + 7 * 86400000));

        const renderWeekCols = (days, showGhost) => (
          days.map((d, i) => {
            const appts = weekAppts(d);
            const isToday = isSameDay(d, now);
            return (
              <WeekCol key={d.toISOString()}>
                {isToday && (
                  <CurrentTimeLine style={{ top: now.getHours() * 60 + now.getMinutes() }} />
                )}
                {getTimelineEvents(appts).map(appt => {
                  const isBlock = appt.isBlock;
                  const cfg = isBlock ? statusConfig['bloqueio'] : (statusConfig[appt.status] || statusConfig['agendado']);
                  const height = Math.max(appt.duration || 60, 20);
                  const maxCols = Math.max(1, Math.max(...getTimelineEvents(appts).map(a => a.colIndex)) + 1);
                  const widthPct = 100 / maxCols;
                  const left = appt.colIndex * widthPct;
                  return (
                    <EventBlock
                      key={appt._id} data-event-block="true"
                      dotColor={cfg.dot} borderColor={isBlock ? '#9ca3af' : cfg.border} bg={isBlock ? '#f3f4f6' : cfg.bg} color={cfg.color}
                      style={{
                        top: appt.startMins, height,
                        left: `${left}%`, width: `${widthPct}%`,
                        zIndex: appt.colIndex + 1, padding: '0.2rem', borderLeftWidth: '3px',
                        ...(isBlock ? { backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)', borderStyle: 'dashed' } : {})
                      }}
                      onClick={(e) => { e.stopPropagation(); setExpanded(appt._id); }}
                      initial={false} animate={{ opacity: 1 }}
                    >
                      <div className="title" style={{ fontSize: '0.65rem' }}>{isBlock ? 'Bloqueio' : appt.clientName}</div>
                      {height >= 40 && <div className="time" style={{ fontSize: '0.55rem' }}>{fmtHour(appt.datetime)}</div>}
                    </EventBlock>
                  );
                })}
                {showGhost && dragGhost && dragGhost.dayIndex === i && (
                  <GhostBlock style={{ top: dragGhost.startMin, height: Math.max(dragGhost.endMin - dragGhost.startMin, 15) }}>
                    <span style={{ fontSize: '0.55rem' }}>{String(Math.floor(dragGhost.startMin / 60)).padStart(2, '0')}:{String(dragGhost.startMin % 60).padStart(2, '0')}</span>
                  </GhostBlock>
                )}
                {showGhost && dragMenu && dragMenu.dayIndex === i && (
                  <DragMenuPopup style={{ top: dragMenu.startMin, flexDirection: 'column', left: 0, right: 0 }}>
                    <DragMenuBtn primary onClick={() => handleDragAction('block')} style={{ fontSize: '0.7rem', padding: '0.4rem' }}>Bloquear</DragMenuBtn>
                    <DragMenuBtn onClick={() => handleDragAction('appointment')} style={{ fontSize: '0.7rem', padding: '0.4rem' }}>Agendar</DragMenuBtn>
                    <DragMenuBtn cancel onClick={() => setDragMenu(null)} style={{ fontSize: '0.7rem', padding: '0.4rem' }}>x</DragMenuBtn>
                  </DragMenuPopup>
                )}
              </WeekCol>
            );
          })
        );

        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TimelineScroll ref={(el) => { timelineRef.current = el; weekSwipeRef.current = el; }} style={{ borderRadius: 0, boxShadow: 'none', margin: 0, height: 'calc(100vh - 200px)' }}>
              <TimelineWrapper>
                <TimeCol>
                  {Array.from({ length: 24 }).map((_, i) => (
                    <HourSlot key={i}>{String(i).padStart(2, '0')}:00</HourSlot>
                  ))}
                </TimeCol>
                <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                  <div
                    style={{
                      display: 'flex',
                      width: '300%',
                      transform: `translateX(calc(-33.333% + ${swipeOffset}px))`,
                      transition: swipeTransition ? 'transform 0.25s ease-out' : 'none',
                      willChange: 'transform'
                    }}
                  >
                    <WeekGrid style={{ minWidth: '33.333%', flex: 'none' }}>
                      {renderWeekCols(prevWeek, false)}
                    </WeekGrid>
                    <WeekGrid ref={weekGridRef} style={{ minWidth: '33.333%', flex: 'none' }}>
                      {renderWeekCols(weekDays, true)}
                    </WeekGrid>
                    <WeekGrid style={{ minWidth: '33.333%', flex: 'none' }}>
                      {renderWeekCols(nextWeek, false)}
                    </WeekGrid>
                  </div>
                </div>
              </TimelineWrapper>
            </TimelineScroll>
          </div>
        );
      })()}

      {/* Formulário novo agendamento */}
      {showForm && (
        <NewAppointmentForm
          selected={selected}
          token={token}
          onClose={() => setShowForm(false)}
          onSaved={(appt) => {
            setAppointments(prev => [...prev, appt]);
            setShowForm(false);
          }}
        />
      )}

      {/* Formulário bloquear horário */}
      {showBlockForm && (
        <BlockTimeForm
          selected={selected}
          token={token}
          onClose={() => setShowBlockForm(false)}
          onSaved={(block) => {
            setAppointments(prev => [...prev, block]);
            setShowBlockForm(false);
          }}
        />
      )}

      {/* Detalhes do agendamento (dia e semana — bottom sheet) */}
      {(view === 'dia' || view === 'semana') && detailAppt && detailAppt.status !== 'cancelado' && (
        <ApptDetailsSheet
          appt={detailAppt}
          onClose={() => setExpanded(null)}
          onAdvance={advanceStatus}
          onCancel={cancelAppt}
        />
      )}
    </Page>
  );
};

// ─── FORMULÁRIO NOVO AGENDAMENTO ─────────────────────────────────────────────
function NewAppointmentForm({ selected, token, onClose, onSaved }) {
  const navigate = useNavigate();
  const pad = (n) => String(n).padStart(2, '0');
  const localDatetime = `${selected.getFullYear()}-${pad(selected.getMonth()+1)}-${pad(selected.getDate())}T10:00`;

  const [catalog, setCatalog]       = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [form, setForm] = useState({
    clientName: '', clientPhone: '', serviceId: '', service: '', price: '',
    duration: '60', datetime: localDatetime, notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCatalog(data.filter(s => s.active)))
      .catch(() => {})
      .finally(() => setLoadingCatalog(false));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleServiceSelect = (e) => {
    const chosen = catalog.find(s => s._id === e.target.value);
    if (chosen) {
      setForm(f => ({ ...f, serviceId: chosen._id, service: chosen.name, price: String(chosen.price), duration: String(chosen.duration) }));
    } else {
      setForm(f => ({ ...f, serviceId: '', service: '', price: '', duration: '60' }));
    }
  };

  const handleSave = async () => {
    if (!form.clientName || !form.service || !form.datetime) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          clientName: form.clientName, clientPhone: form.clientPhone,
          service: form.service,
          price: parseFloat(form.price) || 0,
          duration: parseInt(form.duration) || 60,
          datetime: new Date(form.datetime).toISOString(),
          notes: form.notes
        })
      });
      if (r.ok) onSaved(await r.json());
    } catch {}
    setSaving(false);
  };

  const selectedService = catalog.find(s => s._id === form.serviceId);

  return (
    <Overlay onClick={onClose}>
      <Sheet initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={e => e.stopPropagation()}>
        <h3>Novo agendamento</h3>

        <label>Nome do cliente *</label>
        <input value={form.clientName} onChange={e => set('clientName', e.target.value)} placeholder="Ex: Ana Lima" />

        <label>Telefone</label>
        <input value={form.clientPhone} onChange={e => set('clientPhone', e.target.value)} placeholder="(11) 99999-0000" />

        <label>Serviço *</label>
        {loadingCatalog ? (
          <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: '0 0 0.8rem' }}>Carregando serviços...</p>
        ) : catalog.length === 0 ? (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.8rem' }}>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#92400e' }}>
              Você ainda não tem serviços cadastrados.{' '}
              <button onClick={() => { onClose(); navigate('/editor/servicos'); }}
                style={{ background: 'none', border: 'none', color: '#334155', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: '0.8rem' }}>
                Cadastrar agora →
              </button>
            </p>
          </div>
        ) : (
          <select value={form.serviceId} onChange={handleServiceSelect}>
            <option value="">— selecione um serviço —</option>
            {catalog.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        )}

        {selectedService && (
          <SheetRow>
            <div style={{ flex: 1 }}>
              <label>Valor (R$)</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label>Duração (min)</label>
              <input type="number" min="5" step="5" value={form.duration} onChange={e => set('duration', e.target.value)} />
            </div>
          </SheetRow>
        )}

        <label>Data e hora *</label>
        <input type="datetime-local" value={form.datetime} onChange={e => set('datetime', e.target.value)} />

        <label>Observações</label>
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anotações sobre o atendimento..." />

        <SheetRow>
          <SheetCancel onClick={onClose}>Cancelar</SheetCancel>
          <SheetSave onClick={handleSave} disabled={saving || !form.clientName || !form.service || catalog.length === 0}>
            {saving ? 'Salvando...' : 'Salvar agendamento'}
          </SheetSave>
        </SheetRow>
      </Sheet>
    </Overlay>
  );
}

// ─── FORMULÁRIO BLOQUEAR HORÁRIO ──────────────────────────────────────────────
function BlockTimeForm({ selected, token, onClose, onSaved }) {
  const pad = (n) => String(n).padStart(2, '0');
  const defaultDate = `${selected.getFullYear()}-${pad(selected.getMonth() + 1)}-${pad(selected.getDate())}T${pad(selected.getHours())}:00`;

  const [form, setForm] = useState({ datetime: defaultDate, duration: '60', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.datetime || !form.duration) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/appointments/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          datetime: new Date(form.datetime).toISOString(),
          duration: parseInt(form.duration) || 60,
          notes: form.notes
        })
      });
      if (r.ok) onSaved(await r.json());
    } catch {}
    setSaving(false);
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={e => e.stopPropagation()}>
        <h3>Bloquear horario</h3>

        <label>Data e hora *</label>
        <input type="datetime-local" value={form.datetime} onChange={e => set('datetime', e.target.value)} />

        <label>Duracao (minutos) *</label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
          {['30', '60', '120', '240', '480'].map(d => (
            <button key={d} onClick={() => set('duration', d)}
              style={{
                padding: '0.4rem 0.8rem', borderRadius: '2rem', border: '1.5px solid',
                borderColor: form.duration === d ? '#334155' : '#e5e7eb',
                background: form.duration === d ? '#334155' : 'white',
                color: form.duration === d ? 'white' : '#6b7280',
                fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer'
              }}>
              {d === '30' ? '30min' : d === '60' ? '1h' : d === '120' ? '2h' : d === '240' ? '4h' : 'Dia (8h)'}
            </button>
          ))}
        </div>

        <label>Motivo (opcional)</label>
        <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Ex: Compromisso pessoal" />

        <SheetRow>
          <SheetCancel onClick={onClose}>Cancelar</SheetCancel>
          <SheetSave onClick={handleSave} disabled={saving || !form.datetime}>
            {saving ? 'Salvando...' : 'Bloquear'}
          </SheetSave>
        </SheetRow>
      </Sheet>
    </Overlay>
  );
}

export default Pedidos;
